import { useEffect, useRef, useState } from 'react'
import { AlertErrorCircle, Document, Folder } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import { kUserFilesFileNameMaxLength, kUserFilesFolderNameMaxLength } from '@/user_files/limits'
import { hasInvalidUserFilesNameCharacter, kInvalidUserFilesNameMessage } from '@/user_files/utils/name-validation'
import { splitFilename } from '@/user_files/utils/filename'

export type Conflict = {
  originalName: string
  resolvedName: string
  type?: 'file' | 'folder'
  /**
   * Which target folder this conflict belongs to. Omit when every conflict in the batch shares one
   * destination (e.g. moves, which always have a single destination folder). Required to validate
   * correctly when a single batch can touch multiple folders (e.g. drag-and-drop of several
   * different dropped subfolders at once) — see `occupiedNamesByFolderId`.
   */
  folderId?: string
  /**
   * Human-readable destination for this row (e.g. "Contracts/2024"), shown so rows in a batch that
   * spans several folders aren't indistinguishable — two files named `Report.pdf` headed to
   * different subfolders are both legal and would otherwise render identically.
   */
  folderPathLabel?: string
}

type ConflictModalProps = {
  /** The conflicts to display in the modal. */
  conflicts: Conflict[]
  /** Whether this modal is for a move or upload operation. */
  operation: 'move' | 'upload'
  /** The error to display in the footer. */
  error?: string | null
  /** Whether the modal is currently submitting. */
  isSubmitting?: boolean
  /** The names that are already occupied in the folder. Used as a fallback for conflicts without a `folderId`. */
  occupiedNames: ReadonlySet<string>
  /**
   * Per-folder occupied names, keyed by `Conflict.folderId`. When a conflict carries a `folderId`
   * with an entry here, validation is scoped to that folder instead of the flat `occupiedNames` set —
   * otherwise a name free in this conflict's own folder could be wrongly rejected just because it's
   * taken in a different folder from the same batch.
   */
  occupiedNamesByFolderId?: ReadonlyMap<string, ReadonlySet<string>>
  /** The function to call when the user cancels the modal. */
  onCancel: () => void
  /** The function to call when the user confirms the modal. */
  onConfirm: (resolvedNames: string[]) => void
  /** Whether the modal is open. */
  open: boolean
}

export function ConflictModal({
  open,
  conflicts,
  operation,
  occupiedNames,
  occupiedNamesByFolderId,
  onConfirm,
  onCancel,
  isSubmitting = false,
  error,
}: ConflictModalProps) {
  const fieldLabel = operation === 'upload' ? 'Save upload as' : 'Rename to'
  const primaryActionLabel = operation === 'upload' ? 'Upload all' : 'Rename and move'
  const secondaryActionLabel = operation === 'upload' ? 'Cancel all' : 'Cancel'
  const [names, setNames] = useState<string[]>([])
  const wasOpenRef = useRef(false)

  const exts = conflicts.map((c) => (c.type === 'folder' ? '' : splitFilename(c.resolvedName).ext))

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false
      return
    }

    const justOpened = !wasOpenRef.current
    wasOpenRef.current = true

    setNames((prev) =>
      conflicts.map((c, i) => {
        // Preserve names already edited for rows that existed before this update (e.g. more
        // conflicts appended while the modal is open); only initialize new rows or a fresh open.
        if (!justOpened && prev[i] !== undefined) return prev[i]
        return c.type === 'folder' ? c.resolvedName : splitFilename(c.resolvedName).base
      })
    )
  }, [open, conflicts])

  const updateName = (index: number, value: string) => {
    setNames((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  const getError = (name: string, index: number): string | null => {
    // `names` (state) and `conflicts` (prop) can be transiently out of sync between a conflicts
    // update and the effect that resyncs `names` to match — bail out rather than index past the
    // end of `conflicts`.
    const conflict = conflicts[index]
    if (!conflict) return null

    const trimmed = name.trim()
    if (!trimmed) return 'Name is required'

    const fullName = trimmed + exts[index]
    if (hasInvalidUserFilesNameCharacter(fullName)) return kInvalidUserFilesNameMessage

    const scopedOccupiedNames = conflict.folderId ? occupiedNamesByFolderId?.get(conflict.folderId) : undefined
    const effectiveOccupiedNames = scopedOccupiedNames ?? occupiedNames
    if (effectiveOccupiedNames.has(fullName)) {
      return conflict.type === 'folder' ? 'A folder with this name already exists in this location' : 'A file with this name already exists in this location'
    }

    // Only compare against rows targeting the same folder — two different rows can legitimately
    // share a name if they're headed to different folders.
    const duplicate = names.some((n, i) => {
      if (i === index) return false
      const otherFolderId = conflicts[i]?.folderId
      if (conflict.folderId && otherFolderId && conflict.folderId !== otherFolderId) return false
      return n.trim() + exts[i] === fullName
    })
    if (duplicate) return 'Each item must have a unique name'
    return null
  }

  const errors = names.map((n, i) => getError(n, i))
  const count = conflicts.length
  const canConfirm = names.length === conflicts.length && errors.every((e) => e === null)

  const allFolders = conflicts.every((c) => c.type === 'folder')
  const allFiles = conflicts.every((c) => c.type !== 'folder')
  // A drop can land in the current folder plus several dropped subfolders at once, so there is no
  // single "this location" to point at — each row names its own destination instead.
  const hasMultipleDestinations = new Set(conflicts.map((c) => c.folderId ?? '')).size > 1
  const location = hasMultipleDestinations ? '' : ' in this location'
  const title =
    count === 1
      ? allFolders
        ? `Folder already exists${location}`
        : `File already exists${location}`
      : allFolders
      ? `${count} folders already exist${location}`
      : allFiles
      ? `${count} files already exist${location}`
      : `${count} items already exist${location}`

  let subtitle: string
  if (operation === 'upload') {
    subtitle =
      count === 1
        ? 'Each file must have a unique name. Rename your upload below, or delete the existing file first to replace it.'
        : 'Each file must have a unique name. Rename your uploads below, or delete the existing files first to replace them.'
  } else {
    subtitle =
      count === 1
        ? allFolders
          ? 'A folder with this name already exists in the destination folder. Rename it below before moving.'
          : 'A file with this name already exists in the destination folder. Rename it below before moving.'
        : allFolders
        ? 'Folders with these names already exist in the destination folder. Rename them below before moving.'
        : allFiles
        ? 'Files with these names already exist in the destination folder. Rename them below before moving.'
        : 'Items with these names already exist in the destination folder. Rename them below before moving.'
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <ModalContent
        title={title}
        subtitle={subtitle}
        width="md"
        height="max-full"
        footer={
          <ModalFooterWithActions
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
            secondaryAction={{ label: secondaryActionLabel, onClick: onCancel, disabled: isSubmitting }}
            primaryAction={{
              label: primaryActionLabel,
              onClick: () => onConfirm(names.map((n, i) => n.trim() + exts[i])),
              disabled: !canConfirm || isSubmitting,
              loading: isSubmitting,
            }}
          />
        }
      >
        <div className="border border-brand-neutral-200 rounded-lg overflow-y-auto max-h-72">
          {conflicts.map((conflict, i) => (
            <div key={`${conflict.originalName}-id-${i}`} className="border-t border-brand-neutral-200 first:border-t-0 px-4 pt-0 pb-4">
              <div className="flex items-center gap-[10px] py-4">
                {conflict.type === 'folder' ? (
                  <Folder className="w-6 h-6 shrink-0 text-brand-neutral-950" />
                ) : (
                  <Document className="w-6 h-6 shrink-0 text-brand-neutral-950" />
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-brand-neutral-950 truncate" title={conflict.originalName}>
                    {conflict.originalName}
                  </div>
                  {conflict.folderPathLabel && (
                    <div className="flex items-center gap-1 text-xs text-brand-neutral-600 truncate" title={conflict.folderPathLabel}>
                      <Folder className="w-3 h-3 shrink-0" />
                      <span className="truncate">{conflict.folderPathLabel}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="float-label relative">
                <input
                  id={`conflict-name-${i}`}
                  className={`w-full rounded border-0 ring-1 ring-inset focus:outline-hidden ${
                    errors[i] ? 'ring-red-500 focus:ring-inset focus:ring-red-500' : 'ring-brand-neutral-400 focus:ring-inset focus:ring-brand-400'
                  }`}
                  placeholder=" "
                  value={names[i] ?? ''}
                  maxLength={(conflict.type === 'folder' ? kUserFilesFolderNameMaxLength : kUserFilesFileNameMaxLength) - exts[i].length}
                  onChange={(e) => updateName(i, e.target.value)}
                  onBlur={(e) => updateName(i, e.target.value.trim())}
                />
                <label htmlFor={`conflict-name-${i}`} className="text-brand-neutral-600">
                  {fieldLabel} <span className="text-red-700">*</span>
                </label>
              </div>
              {errors[i] && (
                <div className="flex items-center gap-1 mt-1.5 text-red-700">
                  <AlertErrorCircle className="w-4 h-4 shrink-0" />
                  <span className="text-xs">{errors[i]}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </ModalContent>
    </Modal>
  )
}
